import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import Breadcrums from "../../../common/breadcrumbs";
import * as learnHubServices from "../../../services/learnHub.services";
import { SWAL_SETTINGS } from "../../../../../utils/Constants";
import { getChunks, globalLoader, handleServerValidations } from "../../../../../utils/commonfunction";
import CustomCkeditor from "../../../common/customeditor";
import VideoModal from "./VideoModal";

const EditLearnHub = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [previewThumbnail, setPreviewThumbnail] = useState("");
  const [initialData, setInitialData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showVideoModal, setShowVideoModal] = useState(false);

   const breadcrumbs = [
    { title: "Dashboard", url: "/admin/dashboard" },
    { title: "Learn Hub", url: "/admin/cms/learn-hub/list/1" },
    { title: "View", url: "" },
  ];

  const validationSchema = Yup.object().shape({
    content_type: Yup.string().oneOf(["article", "lesson"]).required("Content type is required"),
    consumer_type: Yup.string().oneOf(["user", "astrologer"]).required("Consumer type is required"),
    title: Yup.string().trim().required("Title is required").max(200, "Title too long"),
    description: Yup.string().trim().required("Description is required"),
    thumbnail: Yup.mixed()
    .test(
      "fileType",
      "Only image files are allowed",
      (value) => {
        if (!value) return true; // 👈 skip validation if no file
        return value.type && value.type.startsWith("image/");
      }
    ).nullable(),

    video_source: Yup.string().when("content_type", {
      is: "lesson",
      then: (schema) => schema.required("Please select video source"),
      otherwise: (schema) => schema.nullable(),
    }),

    // video_file: Yup.mixed().when(["content_type", "video_source", "existing_file_id"], {
    //   is: (content_type, video_source, existing_file_id) =>
    //     content_type === "lesson" && video_source === "upload" && !existing_file_id,
    //   then: (schema) => schema.required("Please upload a video file"),
    //   otherwise: (schema) => schema.nullable(),
    // }),
    video_file: Yup.mixed()
    .nullable()
    .when(["content_type", "video_source", "existing_file_id"], {
      is: (content_type, video_source, existing_file_id) =>
        content_type === "lesson" && 
        video_source === "upload" && 
        !existing_file_id, // Required only if no existing video

      then: (schema) =>
        schema
          .required("Please upload a video file")
          .test("is-mp4", "Only MP4 video files are allowed (.mp4)", (value) => {
            if (!value) return false;
            return value.type === "video/mp4";
          })
          .test("file-extension", "File must have .mp4 extension", (value) => {
            if (!value) return false;
            return value.name.toLowerCase().endsWith(".mp4");
          }),

      otherwise: (schema) => schema.nullable(), // Optional if replacing existing
    }),

    video_link: Yup.string().when(["content_type", "video_source"], {
      is: (content_type, video_source) => content_type === "lesson" && video_source === "youtube",
      then: (schema) => schema.url("Invalid URL").required("YouTube link is required"),
      otherwise: (schema) => schema.nullable(),
    }),
  });

  const formik = useFormik({
    initialValues: {
      content_type: "article",
      consumer_type: "user",
      title: "",
      description: "",
      thumbnail: null,
      video_source: "",
      video_file: null,
      video_link: "",
      existing_thumbnail: "",
      existing_video_filename: "",
      existing_file_id: "",
      streamURL: "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      globalLoader(true);
      setUploading(true);
      setUploadProgress(0);

      try {
        const formData = new FormData();
        formData.append("o_id", id);
        formData.append("content_type", values.content_type);
        formData.append("consumer_type", values.consumer_type);
        formData.append("title", values.title.trim());
        formData.append("description", values.description);

        // Always send video_source so backend knows the intention
        if (values.content_type === "lesson") {
          formData.append("video_source", values.video_source);
        }

        // Thumbnail
        if (values.thumbnail) {
          formData.append("thumbnail", values.thumbnail);
        }

        // Only upload new video if selected
        if (values.content_type === "lesson" && values.video_source === "upload" && values.video_file) {
          const chunks = await getChunks(values.video_file);
          const total = chunks.length;
          const timestamp = Date.now();

          for (let i = 0; i < chunks.length; i++) {
            const chunk = chunks[i];
            const chunkData = new FormData();
            chunkData.append("name", `${timestamp}_${values.video_file.name}`);
            chunkData.append("size", values.video_file.size);
            chunkData.append("type", values.video_file.type);
            chunkData.append("allChunks", total);
            chunkData.append("currentChunk", i);
            chunkData.append("file", chunk);

            const res = await learnHubServices.UploadChunk(chunkData);
            if (!res?.success) throw new Error("Video upload failed");

            if (i === total - 1) {
              formData.append("file_id", res.data.file_id);
              formData.append("video_filename", `${timestamp}_${values.video_file.name}`);
            }
            setUploadProgress(Math.round(((i + 1) / total) * 100));
          }
        }

        // Send YouTube link
        if (values.content_type === "lesson" && values.video_source === "youtube") {
          formData.append("video_link", values.video_link);
        }

        // DO NOT send file_id/video_filename if not uploading new video
        // Backend will preserve existing ones

        const response = await learnHubServices.Edit(formData);

        if (response.success) {
          Swal.fire({ icon: "success", text: response.message, ...SWAL_SETTINGS });
          setTimeout(() => navigate("/admin/cms/learn-hub/list/1"), 1500);
        } else {
          throw new Error(handleServerValidations(response));
        }
      } catch (err) {
        Swal.fire({ icon: "error", text: err.message || "Update failed", ...SWAL_SETTINGS });
      } finally {
        setUploading(false);
        globalLoader(false);
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    globalLoader(true);
    learnHubServices.Details(id)
      .then((res) => {
        if (res?.success) {
          const data = res.data;

          const videoSource = data.content_type === "lesson"
            ? data.video_link ? "youtube" : data.file_id ? "upload" : ""
            : "";

          formik.setValues({
            content_type: data.content_type || "article",
            consumer_type: data.consumer_type || "user",
            title: data.title || "",
            description: data.description || "",
            thumbnail: null,
            video_source: videoSource,
            video_file: null,
            video_link: data.video_link || "",
            existing_thumbnail: data.thumbnail || "",
            existing_video_filename: data.video_filename || 
                                   data.file_id?.original_name || 
                                   data.file_id?.name || 
                                   "Video File",
            existing_file_id: data.file_id?._id || "",
            streamURL: data.streamURL || "",
          });

          setPreviewThumbnail(data.thumbnail || "");
          setInitialData(data);
        } else {
          Swal.fire({ icon: "error", text: res.message || "Failed to load", ...SWAL_SETTINGS });
        }
        globalLoader(false);
      })
      .catch(() => {
        globalLoader(false);
        Swal.fire({ icon: "error", text: "Failed to load content", ...SWAL_SETTINGS });
      });
  }, [id]);

  // console.log("Formik Values:", formik.values); // Debugging line
  // console.log("errors:", formik.errors); // Debugging line

  return (
    <>
      <Breadcrums data={breadcrumbs} />

      {/* VIDEO MODAL - Updated to support both */}
    <VideoModal
    show={showVideoModal}
    onClose={() => setShowVideoModal(false)}
    videoUrl={formik.values.video_source === "upload" ? formik.values.streamURL : null}
    youtubeUrl={formik.values.video_source === "youtube" ? formik.values.video_link : null}
    title={formik.values.title || "Video Preview"}
    />

      {!initialData ? (
        <div className="text-center py-5">Loading content...</div>
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <div className="row">
            <div className="col-12">
              <div className="card shadow-sm">
                <div className="card-body">
                  <h4 className="mb-4 text-primary">Edit Learn Hub Content</h4>

                  {/* Content Type & Consumer Type */}
                  <div className="row mb-4">
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Content Type <span className="text-danger">*</span></label>
                      <select
                        className="form-select"
                        value={formik.values.content_type}
                        onChange={(e) => {
                          formik.handleChange(e);
                          if (e.target.value === "article") {
                            formik.setFieldValue("video_source", "");
                          }
                        }}
                      >
                        <option value="article">Article</option>
                        <option value="lesson">Video Lesson</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Consumer Type <span className="text-danger">*</span></label>
                      <select className="form-select" {...formik.getFieldProps("consumer_type")}>
                        <option value="user">Users</option>
                        <option value="astrologer">Astrologers</option>
                      </select>
                    </div>
                  </div>

                  {/* Title */}
                  <div className="mb-4">
                    <label className="form-label fw-bold">Title <span className="text-danger">*</span></label>
                    <input type="text" className="form-control" placeholder="Enter Title" {...formik.getFieldProps("title")} />
                    {formik.touched.title && formik.errors.title && (
                      <small className="text-danger">{formik.errors.title}</small>
                    )}
                  </div>

                  {/* Description */}
                  <div className="mb-4">
                    <label className="form-label fw-bold">Description <span className="text-danger">*</span></label>
                    <CustomCkeditor
                      fieldname="description"
                      setFieldValue={formik.setFieldValue}
                      setFieldTouched={formik.setFieldTouched}
                      value={formik.values.description}
                    />
                  </div>

                  {/* Thumbnail */}
                  <div className="mb-4">
                    <label className="form-label fw-bold">Thumbnail</label>
                    <input
                      type="file"
                      // accept="image/*"
                      className="form-control"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        formik.setFieldValue("thumbnail", file || null);
                        setPreviewThumbnail(file ? URL.createObjectURL(file) : formik.values.existing_thumbnail);
                      }}
                    />
                    {previewThumbnail && (
                      <div className="mt-3 text-center">
                        <img
                          src={previewThumbnail}
                          alt="Thumbnail"
                          className="img-thumbnail"
                          style={{ maxHeight: "200px", objectFit: "cover" }}
                        />
                        <p className="text-muted small mt-2">
                          {formik.values.thumbnail ? "New thumbnail" : "Current thumbnail"}
                        </p>
                      </div>
                    )}
                    {formik.touched.thumbnail && formik.errors.thumbnail && (
                      <small className="text-danger">{formik.errors.thumbnail}</small>
                    )}
                  </div>

                  {/* Video Section */}
                  {formik.values.content_type === "lesson" && (
                    <div className="border rounded p-4 bg-light mb-4">
                      <h6 className="fw-bold mb-3">Video Source <span className="text-danger">*</span></h6>

                      <div className="row mb-4">
                        <div className="col-md-6">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              value="upload"
                              checked={formik.values.video_source === "upload"}
                              onChange={() => formik.setFieldValue("video_source", "upload")}
                            />
                            <label className="form-check-label fw-bold">Upload Video</label>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              value="youtube"
                              checked={formik.values.video_source === "youtube"}
                              onChange={() => {
                                formik.setFieldValue("video_source", "youtube");
                                formik.setFieldValue("video_file", null);
                              }}
                            />
                            <label className="form-check-label fw-bold">YouTube Link</label>
                          </div>
                        </div>
                      </div>

                      {/* Current Video */}
                    {formik.values.content_type === "lesson" && (
                      (formik.values.video_source === "upload" && formik.values.streamURL) ||
                      (formik.values.video_source === "youtube" && formik.values.video_link)
                    ) && (
                    <div className="alert alert-success p-4 rounded mb-4">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div>
                            <strong>Current Video:</strong><br />
                            <span className="text-primary">
                            {formik.values.video_source === "upload" 
                                ? formik.values.existing_video_filename 
                                : "YouTube Video"}
                            </span>
                        </div>
                        <button
                            type="button"
                            className="btn btn-outline-primary d-flex align-items-center gap-2"
                            onClick={() => setShowVideoModal(true)}
                        >
                            Play Preview
                        </button>
                        </div>
                    </div>
                    )}

                      {/* Replace Video */}
                      {/* {formik.values.video_source === "upload" && (
                        <div>
                          <label className="form-label">
                            {formik.values.existing_file_id ? "Replace Video (Optional)" : "Upload Video *"}
                          </label>
                          <input
                            type="file"
                            accept="video/*"
                            className="form-control"
                            onChange={(e) => formik.setFieldValue("video_file", e.target.files[0] || null)}
                          />
                          {formik.values.video_file && (
                            <small className="text-success d-block mt-2">
                              Selected: <strong>{formik.values.video_file.name}</strong>
                            </small>
                          )}
                        </div>
                      )} */}
                      {formik.values.video_source === "upload" && (
                        <div>
                          <label className="form-label fw-bold">
                            {formik.values.existing_file_id 
                              ? "Replace Video File (Optional)" 
                              : "Upload Video File"
                            }
                            {!formik.values.existing_file_id && <span className="text-danger"> *</span>}
                          </label>

                          <input
                            type="file"
                            accept="video/mp4,.mp4" // Browser-level filter: only shows MP4 files
                            className={`form-control ${
                              formik.touched.video_file && formik.errors.video_file ? "is-invalid" : ""
                            }`}
                            onChange={(e) => {
                              const file = e.target.files[0];

                              if (!file) {
                                formik.setFieldValue("video_file", null);
                                return;
                              }

                              // Double-check type and extension (defense in depth)
                              const isValidType = file.type === "video/mp4";
                              const isValidExt = file.name.toLowerCase().endsWith(".mp4");

                              if (!isValidType || !isValidExt) {
                                formik.setFieldValue("video_file", null);
                                formik.setFieldError(
                                  "video_file",
                                  "Invalid file type. Please upload only MP4 video files (.mp4)"
                                );

                                Swal.fire({
                                  icon: "warning",
                                  title: "Invalid File Type",
                                  text: "Only MP4 video files are allowed. Please select a .mp4 file.",
                                  ...SWAL_SETTINGS,
                                });
                                return;
                              }

                              // File is valid → accept it
                              formik.setFieldValue("video_file", file);
                            }}
                          />

                          {/* Show selected valid file */}
                          {formik.values.video_file && (
                            <div className="mt-3 p-3 bg-success bg-opacity-10 border border-success rounded">
                              <small className="text-success">
                                <strong>Selected:</strong> {formik.values.video_file.name}
                              </small>
                              <br />
                              <small className="text-muted">
                                Type: {formik.values.video_file.type} • Size: {(formik.values.video_file.size / (1024 * 1024)).toFixed(2)} MB
                              </small>
                            </div>
                          )}

                          {/* Formik Error Message */}
                          {formik.touched.video_file && formik.errors.video_file && (
                            <div className="invalid-feedback d-block mt-2">
                              {formik.errors.video_file}
                            </div>
                          )}

                          {/* Help Text */}
                          <small className="text-muted d-block mt-2">
                            <strong>Only MP4 video files (.mp4) are supported.</strong><br />
                            Other formats like MOV, AVI, WebM, MKV, images, or PDFs are not allowed.
                          </small>
                        </div>
                      )}

                      {/* YouTube */}
                      {formik.values.video_source === "youtube" && (
                        <div>
                          <label className="form-label">YouTube URL *</label>
                          <input
                            type="url"
                            className="form-control"
                            placeholder="https://www.youtube.com/watch?v=..."
                            {...formik.getFieldProps("video_link")}
                          />
                          {formik.touched.video_link && formik.errors.video_link && (
                            <small className="text-danger">{formik.errors.video_link}</small>
                          )}
                        </div>
                      )}

                      {uploading && (
                        <div className="mt-4">
                          <div className="progress" style={{ height: "40px" }}>
                            <div
                              className="progress-bar progress-bar-striped progress-bar-animated bg-success"
                              style={{ width: `${uploadProgress}%` }}
                            >
                              <strong>{uploadProgress}% Uploading...</strong>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mt-5 d-flex gap-3">
                    <button
                      type="submit"
                      disabled={uploading || formik.isSubmitting}
                      className="btn btn-primary btn-lg px-5"
                    >
                      {uploading ? `Updating... ${uploadProgress}%` : "Update Content"}
                    </button>
                    <button type="button" onClick={() => navigate(-1)} className="btn btn-secondary btn-lg px-5">
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      )}
    </>
  );
};

export default EditLearnHub;